import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../hooks/useTheme";
import { Search, PencilIcon, UserX, Plus, UserCheck, MailCheck, Eye, EyeOff, Users, CheckCircle, XCircle, Link, Award, UserCircle, ExternalLink } from "lucide-react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from 'react-router-dom';
import api from "../../api/api";


// One label/value row used in the coach Credential / Profile modals.
function DetailRow({ label, children }) {
    return (
        <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100 last:border-0">
            <span className="text-slate-500 shrink-0">{label}</span>
            <span className="font-medium text-right break-all">{children}</span>
        </div>
    );
}

// Edit Employee Modal
function EditEmployeeModal({ open, employee, onClose, onSave }) {
    const { isDarkMode } = useTheme();
    const [email, setEmail] = useState(employee?.Email || '');
    const [role, setRole] = useState(employee?.AdminTypeID || 0);

    React.useEffect(() => {
        setEmail(employee?.Email || '');
        setRole(employee?.AdminTypeID || 0);
    }, [employee]);

    if (!open) return null;
    return (
        <div className="modal-overlay">
            <div className={`modal-content ${isDarkMode ? 'bg-slate-800 text-slate-100' : ''}`}>
                <h2 className="text-lg font-bold mb-4">Edit Employee</h2>
                <div className="mb-4">
                    <label htmlFor="edit-employee-email" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>Email</label>
                    <input
                        id="edit-employee-email"
                        name="email"
                        type="email"
                        className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : ''}`}
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="edit-employee-role" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>Role</label>
                    <select
                        id="edit-employee-role"
                        name="role"
                        className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                        value={role}
                        onChange={e => setRole(Number(e.target.value))}
                    >
                        <option value={0}>Default</option>
                        <option value={1}>Admin</option>
                        <option value={2}>IT Manager</option>
                        <option value={3}>View Only</option>
                        <option value={4}>Owner</option>
                        <option value={5}>Support</option>
                        <option value={6}>Coach</option>
                    </select>
                </div>
                <div className="flex gap-2 justify-end">
                    <button className={`px-3 py-1 rounded ${isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`} onClick={onClose}>Cancel</button>
                    <button className="px-3 py-1 rounded bg-primary-600 text-white" onClick={() => onSave({
                        AdminID: employee.AdminID,
                        Email: email,
                        AdminTypeID: role
                    })}>Save</button>
                </div>
            </div>
        </div>
    );
}

export default EmployeeManagement;

// Main EmployeeManagement component

function EmployeeManagement() {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [activeTab, setActiveTab] = useState('employees'); // 'employees', 'partners', or 'permissions'
    const [sortKey, setSortKey] = useState('name'); // 'name' or 'role'
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeOnly, setActiveOnly] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [addAdminData, setAddAdminData] = useState({ first_name: '', last_name: '', email: '', password: '', role: 1 });
    const [showAddEmployeePassword, setShowAddEmployeePassword] = useState(false);
    const [resetConfirm, setResetConfirm] = useState({ open: false, employee: null });
    const [resetSuccess, setResetSuccess] = useState(false);
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnerSearch, setPartnerSearch] = useState("");

    // Employer Applications tab state
    const [employerApps, setEmployerApps] = useState([]);
    const [employerAppsLoading, setEmployerAppsLoading] = useState(false);
    const [employerAppsFilter, setEmployerAppsFilter] = useState('pending');
    const [rejectModal, setRejectModal] = useState({ open: false, appId: null, reason: '' });
    const [approveModal, setApproveModal] = useState({ open: false, mode: 'approve', app: null, employerId: '', search: '', employers: [], loadingEmployers: false });
    const [approveCoachConfirm, setApproveCoachConfirm] = useState({ open: false, partner: null });
    // Approve a coach who never verified their email (admin ticked the override)
    const [approveCoachOverride, setApproveCoachOverride] = useState(false);
    const [credentialModal, setCredentialModal] = useState({ open: false, partner: null });
    const [profileModal, setProfileModal] = useState({ open: false, partner: null });
    
    const handlePasswordReset = (employee) => {
        setResetConfirm({ open: true, employee });
    };

    const handleConfirmReset = async () => {
        if (!resetConfirm.employee?.Email) return;
        try {
            await api.post("/auth/admin/send-password-reset", { email: resetConfirm.employee.Email });
            setResetConfirm({ open: false, employee: null });
            setResetSuccess(true);
            setTimeout(() => setResetSuccess(false), 2000);
        } catch (err) {
            setResetConfirm({ open: false, employee: null });
            alert("Failed to send password reset email.");
        }
    };

    const handleCancelReset = () => {
        setResetConfirm({ open: false, employee: null });
    };

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/');
        }
    }, [user, navigate]);

        // Edit handler
    const handleEdit = (emp) => {
        setEditingEmployee(emp);
        setEditModalOpen(true);
    };

    // Save edit handler
    const handleSaveEdit = async ({ AdminID, Email, AdminTypeID }) => {
        try {
            await api.put(`/admin/employees/${AdminID}`, { email: Email, role: AdminTypeID });
            setEditModalOpen(false);
            fetchEmployees();
        } catch (err) {
        }
    };
    
    // Deactivate handler
    const handleDeactivate = async (employee) => {
        if (!window.confirm('Are you sure you want to deactivate this employee?')) return;
        try {
            await api.put(`/admin/employees/${employee.AdminID}/deactivate`, { isActive: 0 });
            fetchEmployees();
        } catch (err) {
            
        }
    };

        // Reactivate handler
    const handleReactivate = async (employee) => {
        try {
            await api.put(`/admin/employees/${employee.AdminID}/reactivate`);
            fetchEmployees();
        } catch (err) {
           
        }
    };

    const fetchEmployees = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/admin/employees`, { params: { page } });
            const data = res.data;
            setEmployees(data.employees || []);
            setTotal(data.total || 0);
            setTotalPages(data.total_pages || 1);
        } catch (err) {
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    const fetchPartners = useCallback(async () => {
        setPartnersLoading(true);
        try {
            const res = await api.get('/admin/partners');
            setPartners(res.data.partners || []);
        } catch {
            setPartners([]);
        } finally {
            setPartnersLoading(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'partners') fetchPartners();
    }, [activeTab, fetchPartners]);

    const togglePartnerVisibility = async (partner) => {
        try {
            await api.put(`/admin/partners/${partner.PartnerID}/visibility`, { is_visible: !partner.IsVisible });
            fetchPartners();
        } catch { }
    };

    // Approve a pending coach: activates the account (they can sign in) and, on
    // the backend, also un-hides their Partners row so they appear in Find a Coach.
    // Uses the same in-app confirmation modal pattern as Send Password Reset.
    //old code
    //const handleActivatePartner = (partner) => setApproveCoachConfirm({ open: true, partner });
    const handleActivatePartner = (partner) => {
        setApproveCoachOverride(false);
        setApproveCoachConfirm({ open: true, partner });
    };
    const handleCancelActivate = () => setApproveCoachConfirm({ open: false, partner: null });
    const handleConfirmActivate = async () => {
        const partner = approveCoachConfirm.partner;
        setApproveCoachConfirm({ open: false, partner: null });
        if (!partner) return;
        try {
            //old code
            //await api.put(`/admin/employees/${partner.AdminID}/reactivate`);
            await api.put(`/admin/employees/${partner.AdminID}/reactivate`, approveCoachOverride ? { force: true } : {});
            fetchPartners();
        //old code
        //} catch { }
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to approve coach.');
        }
    };

    const fetchEmployerApps = useCallback(async () => {
        setEmployerAppsLoading(true);
        try {
            const res = await api.get('/employer/admin/applications', {
                params: employerAppsFilter ? { status: employerAppsFilter } : {}
            });
            setEmployerApps(res.data.applications || []);
        } catch (err) {
            setEmployerApps([]);
        } finally {
            setEmployerAppsLoading(false);
        }
    }, [employerAppsFilter]);

    useEffect(() => {
        if (activeTab === 'employer-applications') fetchEmployerApps();
    }, [activeTab, fetchEmployerApps]);

    const openApproveModal = async (app, mode = 'approve') => {
        setApproveModal({ open: true, mode, app, employerId: '', search: '', employers: [], loadingEmployers: true });
        try {
            const res = await api.get('/jobs/employers');
            setApproveModal(m => ({ ...m, employers: res.data.employers || [], loadingEmployers: false }));
        } catch {
            setApproveModal(m => ({ ...m, loadingEmployers: false }));
        }
    };

    const handleApproveEmployer = async () => {
        const { app, employerId, mode } = approveModal;
        const endpoint = mode === 'link'
            ? `/employer/admin/applications/${app.ApplicationID}/link-company`
            : `/employer/admin/applications/${app.ApplicationID}/approve`;
        try {
            await api.post(endpoint, {
                employer_id: employerId ? Number(employerId) : null
            });
            setApproveModal({ open: false, mode: 'approve', app: null, employerId: '', search: '', employers: [], loadingEmployers: false });
            fetchEmployerApps();
        } catch (err) {
            alert(err.response?.data?.error || (mode === 'link' ? 'Failed to link company.' : 'Failed to approve application.'));
        }
    };

    const handleRejectEmployer = async () => {
        try {
            await api.post(`/employer/admin/applications/${rejectModal.appId}/reject`, {
                reason: rejectModal.reason
            });
            setRejectModal({ open: false, appId: null, reason: '' });
            fetchEmployerApps();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to reject application.');
        }
    };


    // Role mapping
    const getRoleName = (roleId) => {
        switch (Number(roleId)) {
            case 1:
                return 'Admin';
            case 2:
                return 'IT Manager';
            case 3:
                return 'View Only';
            case 0:
                return 'Default';
            case 4:
                return 'Owner';
            case 5: 
                return "Support";
            case 6: 
                return "Coach";
            default:
                return String(roleId);
        }
    };

    // Filter employees by search, activeOnly, and exclude coaches (shown in Partners tab)
    let filteredEmployees = employees.filter(emp => {
        if (Number(emp.AdminTypeID) === 6) return false;
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
            emp.FirstName?.toLowerCase().includes(searchLower) ||
            emp.LastName?.toLowerCase().includes(searchLower) ||
            emp.Email?.toLowerCase().includes(searchLower);
        const matchesActive = activeOnly ? emp.IsActive : true;
        return matchesSearch && matchesActive;
    });

    // Sort employees by selected key and direction
    filteredEmployees = filteredEmployees.slice().sort((a, b) => {
        let valA, valB;
        if (sortKey === 'name') {
            valA = (a.FirstName + ' ' + a.LastName).toLowerCase();
            valB = (b.FirstName + ' ' + b.LastName).toLowerCase();
        } else if (sortKey === 'role') {
            valA = getRoleName(a.AdminTypeID)?.toLowerCase() || '';
            valB = getRoleName(b.AdminTypeID)?.toLowerCase() || '';
        } else {
            valA = '';
            valB = '';
        }
        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    // Loading spinner for employees tab
    const loadingSpinner = (
        <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
    );
    
    // Add Admin handler
    const handleAddAdmin = async () => {
        try {
            console.log('Add Admin Payload:', addAdminData);
            await api.post('/admin/create-admin', addAdminData);
            setAddModalOpen(false);
            setAddAdminData({ first_name: '', last_name: '', email: '', password: '', role: 1 });
            fetchEmployees();
        } catch (err) {
            // Optionally show error
        }
    };

    // Permission Matrix Table as inner function
    function PermissionMatrixTable() {
        const roleToAdminTypeId = {
            'Admin': 1,
            'IT Manager': 2,
            'View Only': 3,
            'Default': 0,
            'Owner': 4,
            'Support': 5,
            'Coach': 6,
            'Employer': 7
        };

        function flattenMatrix() {
            const arr = [];
            Object.entries(matrix).forEach(([cat, subcats]) => {
                Object.entries(subcats).forEach(([sub, rolesObj]) => {
                    Object.entries(rolesObj).forEach(([role, perms]) => {
                        Object.entries(perms).forEach(([perm, val]) => {
                            arr.push({
                                permission_id: null,
                                admin_type_id: roleToAdminTypeId[role],
                                category: cat,
                                subcategory: sub,
                                permission: perm,
                                value: !!val
                            });
                        });
                    });
                });
            });
            return arr;
        }

        const [saving, setSaving] = useState(false);
        const [saveStatus, setSaveStatus] = useState(null);
        async function handleSave() {
            setSaving(true);
            setSaveStatus(null);
            try {
                await api.post('/permissions', flattenMatrix());
                setSaveStatus('success');
                fetchPermissions();
                setTimeout(() => setSaveStatus(null), 3000);
            } catch (err) {
                setSaveStatus('error');
            } finally {
                setSaving(false);
            }
        }

        const roles = ["Admin", "IT Manager", "View Only", "Support", "Coach", "Employer"];
        const [selectedRole, setSelectedRole] = useState(roles[0]);

        const categories = [
            { name: "Employees", subcategories: ["Employee Management", "Permission Matrix"], permissions: ["view", "edit", "delete"] },
            { name: "Users", subcategories: ["User Management", "Email", "Role Configuration"], permissions: ["view", "edit", "delete"] },
            { name: "Lookups", subcategories: ["Personas", "Intents", "Industries", "Positions", "Onboarding"], permissions: ["view", "edit", "delete"] },
            { name: "Memberships", subcategories: ["Membership Tiers", "Adding Memberships", "Payment Link"], permissions: ["view", "edit", "delete"] },
            { name: "Other", subcategories: [], permissions: ["view", "edit", "delete"] }
        ];

        const initialMatrix = {};
        categories.forEach(cat => {
            initialMatrix[cat.name] = {};
            cat.subcategories.forEach(sub => {
                initialMatrix[cat.name][sub] = {};
                roles.forEach(role => {
                    initialMatrix[cat.name][sub][role] = {};
                    cat.permissions.forEach(perm => {
                        initialMatrix[cat.name][sub][role][perm] = false;
                    });
                });
            });
        });

        const [matrix, setMatrix] = useState(() => JSON.parse(JSON.stringify(initialMatrix)));
        const [loadingPerms, setLoadingPerms] = useState(false);

        const fetchPermissions = React.useCallback(() => {
            let isMounted = true;
            async function doFetch() {
                setLoadingPerms(true);
                try {
                    const res = await api.get('/permissions');
                    const perms = res.data;
                    const adminTypeIdToRole = { 1: 'Admin', 2: 'IT Manager', 3: 'View Only', 0: 'Default', 4: 'Owner', 5: 'Support', 6: 'Coach', 7: 'Employer' };
                    const newMatrix = JSON.parse(JSON.stringify(initialMatrix));
                    perms.forEach(perm => {
                        const role = adminTypeIdToRole[perm.admin_type_id];
                        const cat = categories.find(c => c.name === perm.category);
                        const subcat = perm.subcategory;
                        if (cat && newMatrix[cat.name][subcat] && newMatrix[cat.name][subcat][role]) {
                            newMatrix[cat.name][subcat][role][perm.permission] = perm.value === 1;
                        }
                    });
                    if (isMounted) {
                        // Ensure all values are boolean
                        Object.keys(newMatrix).forEach(cat => {
                            Object.keys(newMatrix[cat]).forEach(subcat => {
                                Object.keys(newMatrix[cat][subcat]).forEach(role => {
                                    Object.keys(newMatrix[cat][subcat][role]).forEach(perm => {
                                        if (typeof newMatrix[cat][subcat][role][perm] === 'object') {
                                            newMatrix[cat][subcat][role][perm] = false;
                                        }
                                    });
                                });
                            });
                        });
                        setMatrix(newMatrix);
                    }
                } catch (err) {
                    // Error fetching permissions
                } finally {
                    if (isMounted) setLoadingPerms(false);
                }
            }
            doFetch();
            return () => { isMounted = false; };
        }, [activeTab]);

        React.useEffect(() => {
            if (activeTab === 'permissions') fetchPermissions();
        }, [activeTab, fetchPermissions]);

        const [expanded, setExpanded] = useState(categories.map(() => false));

        if (loadingPerms) {
            return (
                <div className="flex items-center justify-center min-h-[200px]">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
                </div>
            );
        }

        const handleToggle = (catIdx, sub, perm) => {
            setMatrix(prev => {
                const catName = categories[catIdx].name;
                const newMatrix = { ...prev };
                newMatrix[catName] = { ...prev[catName] };
                newMatrix[catName][sub] = { ...prev[catName][sub] };
                newMatrix[catName][sub][selectedRole] = { ...prev[catName][sub][selectedRole] };
                newMatrix[catName][sub][selectedRole][perm] = !prev[catName][sub][selectedRole][perm];
                return newMatrix;
            });
        };

        const handleExpand = idx => {
            setExpanded(prev => prev.map((v, i) => i === idx ? !v : v));
        };

        const getMainCheckboxState = (catIdx, perm) => {
            const cat = categories[catIdx];
            if (cat.subcategories.length === 0) return "unchecked";
            const values = cat.subcategories.map(sub => matrix[cat.name][sub][selectedRole][perm]);
            const all = values.every(Boolean);
            const none = values.every(v => !v);
            if (all) return "checked";
            if (none) return "unchecked";
            return "indeterminate";
        };

        const handleMainToggle = (catIdx, perm) => {
            setMatrix(prev => {
                const cat = categories[catIdx];
                const state = getMainCheckboxState(catIdx, perm);
                const newValue = state === "checked" ? false : true;
                const newMatrix = { ...prev };
                newMatrix[cat.name] = { ...prev[cat.name] };
                cat.subcategories.forEach(sub => {
                    newMatrix[cat.name][sub] = { ...prev[cat.name][sub] };
                    newMatrix[cat.name][sub][selectedRole] = { ...prev[cat.name][sub][selectedRole] };
                    newMatrix[cat.name][sub][selectedRole][perm] = newValue;
                });
                return newMatrix;
            });
        };

        const permIcons = { view: '👁', edit: '✏️', delete: '🗑' };

        return (
            <div className={`rounded-2xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                {/* Header bar */}
                <div className={`flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-3">
                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Role:</span>
                        <div className="flex gap-1.5 flex-wrap">
                            {roles.map(role => (
                                <button
                                    key={role}
                                    onClick={() => setSelectedRole(role)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                        selectedRole === role
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : isDarkMode
                                                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {role}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {saveStatus === 'success' && (
                            <span className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                Saved
                            </span>
                        )}
                        {saveStatus === 'error' && (
                            <span className="text-sm font-medium text-red-500">Failed to save</span>
                        )}
                        <button
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                                saving
                                    ? 'bg-primary-400 text-white cursor-not-allowed'
                                    : 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                            }`}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Matrix table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className={isDarkMode ? 'bg-slate-700/50' : 'bg-slate-50'}>
                                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Permission Category
                                </th>
                                {categories[0].permissions.map(perm => (
                                    <th className={`px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider w-24 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} key={perm}>
                                        <span className="flex items-center justify-center gap-1.5">
                                            {permIcons[perm]} {perm}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700/50' : 'divide-slate-100'}`}>
                            {categories.map((cat, catIdx) => (
                                <React.Fragment key={cat.name}>
                                    {/* Category header row */}
                                    <tr className={`cursor-pointer transition-colors ${
                                        isDarkMode
                                            ? 'bg-slate-800 hover:bg-slate-750'
                                            : 'bg-white hover:bg-slate-50'
                                    }`}
                                        onClick={() => handleExpand(catIdx)}
                                    >
                                        <td className="px-6 py-3">
                                            <div className="flex items-center gap-2">
                                                <svg
                                                    className={`w-4 h-4 transition-transform ${expanded[catIdx] ? 'rotate-90' : ''} ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                                <span className={`text-sm font-bold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                    {cat.name}
                                                </span>
                                                {cat.subcategories.length > 0 && (
                                                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                                                        {cat.subcategories.length}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        {cat.permissions.map(perm => {
                                            const state = getMainCheckboxState(catIdx, perm);
                                            return (
                                                <td className="px-4 py-3 text-center" key={perm} onClick={e => e.stopPropagation()}>
                                                    <div className="flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={state === "checked"}
                                                            onChange={() => handleMainToggle(catIdx, perm)}
                                                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                            ref={el => { if (el) el.indeterminate = state === "indeterminate"; }}
                                                        />
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {/* Expanded subcategory rows */}
                                    {expanded[catIdx] && cat.subcategories.map(sub => (
                                        <tr key={sub} className={`transition-colors ${isDarkMode ? 'bg-slate-800/60 hover:bg-slate-700/40' : 'bg-slate-50/50 hover:bg-slate-50'}`}>
                                            <td className="py-2.5">
                                                <span className={`pl-14 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                    {sub}
                                                </span>
                                            </td>
                                            {cat.permissions.map(perm => (
                                                <td className="px-4 py-2.5 text-center" key={perm}>
                                                    <div className="flex justify-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(matrix[cat.name][sub][selectedRole][perm])}
                                                            onChange={() => handleToggle(catIdx, sub, perm)}
                                                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                        />
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div className={`p-6 ${isDarkMode ? 'bg-slate-900 text-slate-100' : ''}`}>
            <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Employee Management</h1>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage employees and view permissions matrix</p>
            <div className={`flex border-b mb-6 ${isDarkMode ? 'border-slate-700' : ''}`}>
                <button
                    className={`px-4 py-2 -mb-px border-b-2 font-medium focus:outline-none ${activeTab === 'employees' ? 'border-blue-600 text-blue-600' : isDarkMode ? 'border-transparent text-slate-400' : 'border-transparent text-slate-500'}`}
                    onClick={() => setActiveTab('employees')}
                >
                    Employees
                </button>
                <button
                    className={`ml-4 px-4 py-2 -mb-px border-b-2 font-medium focus:outline-none ${activeTab === 'partners' ? 'border-blue-600 text-blue-600' : isDarkMode ? 'border-transparent text-slate-400' : 'border-transparent text-slate-500'}`}
                    onClick={() => setActiveTab('partners')}
                >
                    Partners
                </button>
                <button
                    className={`ml-4 px-4 py-2 -mb-px border-b-2 font-medium focus:outline-none ${activeTab === 'permissions' ? 'border-blue-600 text-blue-600' : isDarkMode ? 'border-transparent text-slate-400' : 'border-transparent text-slate-500'}`}
                    onClick={() => setActiveTab('permissions')}
                >
                    Permissions Matrix
                </button>
                <button
                    className={`ml-4 px-4 py-2 -mb-px border-b-2 font-medium focus:outline-none ${activeTab === 'employer-applications' ? 'border-blue-600 text-blue-600' : isDarkMode ? 'border-transparent text-slate-400' : 'border-transparent text-slate-500'}`}
                    onClick={() => setActiveTab('employer-applications')}
                >
                    Employer Applications
                </button>
            </div>
            {activeTab === 'employees' && (
                loading && employees.length === 0 ? loadingSpinner : (
                    <>
                        {/* Add Employee Modal */}
                        {addModalOpen && (
                            <div className="modal-overlay">
                                <div className={`modal-content ${isDarkMode ? 'bg-slate-800 text-slate-100' : ''}`}>
                                    <h2 className="text-lg font-bold mb-4">Add Employee</h2>
                                    <div className="mb-2">
                                        <label htmlFor="add-employee-first-name" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>First Name</label>
                                        <input id="add-employee-first-name" name="first_name" type="text" className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : ''}`} value={addAdminData.first_name} onChange={e => setAddAdminData({ ...addAdminData, first_name: e.target.value })} />
                                    </div>
                                    <div className="mb-2">
                                        <label htmlFor="add-employee-last-name" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>Last Name</label>
                                        <input id="add-employee-last-name" name="last_name" type="text" className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : ''}`} value={addAdminData.last_name} onChange={e => setAddAdminData({ ...addAdminData, last_name: e.target.value })} />
                                    </div>
                                    <div className="mb-2">
                                        <label htmlFor="add-employee-email" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>Email</label>
                                        <input id="add-employee-email" name="email" type="email" className={`w-full border rounded px-3 py-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : ''}`} value={addAdminData.email} onChange={e => setAddAdminData({ ...addAdminData, email: e.target.value })} />
                                    </div>
                                    <div className="mb-2">
                                        <label htmlFor="add-employee-password" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>Password</label>
                                        <div className="relative">
                                            <input id="add-employee-password" name="password" type={showAddEmployeePassword ? 'text' : 'password'} className={`w-full border rounded px-3 py-2 pr-10 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200' : ''}`} value={addAdminData.password} onChange={e => setAddAdminData({ ...addAdminData, password: e.target.value })} />
                                            <button type="button" onClick={() => setShowAddEmployeePassword(prev => !prev)} className={`absolute right-2.5 top-1/2 -translate-y-1/2 transition-colors ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'}`} aria-label={showAddEmployeePassword ? 'Hide password' : 'Show password'} tabIndex={-1}>
                                                {showAddEmployeePassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <label htmlFor="add-employee-role" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : ''}`}>Role</label>
                                        <select id="add-employee-role" name="role" className={`select-styled w-full text-sm font-medium rounded-xl px-3 py-2.5 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`} value={addAdminData.role} onChange={e => setAddAdminData({ ...addAdminData, role: Number(e.target.value) })}>
                                            <option value={1}>Admin</option>
                                            <option value={2}>IT Manager</option>
                                            <option value={3}>View Only</option>
                                            <option value={0}>Default</option>
                                            <option value={4}>Owner</option>
                                            <option value={5}>Support</option>
                                            <option value={6}>Coach</option>
                                        </select>
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                        <button className={`px-3 py-1 rounded ${isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`} onClick={() => setAddModalOpen(false)}>Cancel</button>
                                        <button className="px-3 py-1 rounded bg-primary-600 text-white" onClick={handleAddAdmin}>Add</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Edit Employee Modal */}
                        {editModalOpen && (
                            <EditEmployeeModal
                                open={editModalOpen}
                                employee={editingEmployee}
                                onClose={() => setEditModalOpen(false)}
                                onSave={handleSaveEdit}
                            />
                        )}

                        <div className="flex items-center mb-4">
                            <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded mr-4" onClick={() => setAddModalOpen(true)}>
                                <Plus className="w-4 h-4 mr-2" /> Add Employee
                            </button>
                            <input
                                type="text"
                                placeholder="Search employees..."
                                className={`border rounded px-3 py-2 mr-2 pl-8 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-500' : ''}`}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{ position: 'relative' }}
                            />
                            <span style={{ position: 'relative', left: '-2.2rem', pointerEvents: 'none' }}>
                                <Search className="w-4 h-4 text-gray-400" />
                            </span>
                            <label className={`flex items-center text-sm ml-2 ${isDarkMode ? 'text-slate-300' : ''}`}>
                                <input type="checkbox" className="mr-1" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
                                Active Only
                            </label>
                        </div>

                        <div className={`overflow-x-auto rounded shadow ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                            <table className={`min-w-full divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-200'}`}>
                                <thead className={isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}>
                                    <tr>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}
                                            onClick={() => {
                                                if (sortKey === 'name') {
                                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                                } else {
                                                    setSortKey('name');
                                                    setSortDirection('asc');
                                                }
                                            }}
                                        >
                                            Name
                                            <span className="ml-1">
                                                <span className={sortKey === 'name' && sortDirection === 'asc' ? 'font-bold text-blue-600' : isDarkMode ? 'text-slate-500' : 'text-gray-400'}>▲</span>
                                                <span className={sortKey === 'name' && sortDirection === 'desc' ? 'font-bold text-blue-600' : isDarkMode ? 'text-slate-500' : 'text-gray-400'}>▼</span>
                                            </span>
                                        </th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Email</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}
                                            onClick={() => {
                                                if (sortKey === 'role') {
                                                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                                                } else {
                                                    setSortKey('role');
                                                    setSortDirection('asc');
                                                }
                                            }}
                                        >
                                            Role
                                            <span className="ml-1">
                                                <span className={sortKey === 'role' && sortDirection === 'asc' ? 'font-bold text-blue-600' : isDarkMode ? 'text-slate-500' : 'text-gray-400'}>▲</span>
                                                <span className={sortKey === 'role' && sortDirection === 'desc' ? 'font-bold text-blue-600' : isDarkMode ? 'text-slate-500' : 'text-gray-400'}>▼</span>
                                            </span>
                                        </th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Last Login</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Status</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`} style={{ minWidth: '180px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'bg-slate-800 divide-slate-700' : 'bg-white divide-gray-200'}`}>
                                    {filteredEmployees.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className={`px-6 py-4 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>No employees found.</td>
                                        </tr>
                                    ) : (
                                        filteredEmployees.map(emp => (
                                            <tr key={emp.AdminID}>
                                                <td className="px-6 py-4 whitespace-nowrap">{emp.FirstName} {emp.LastName}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{emp.Email}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{getRoleName(emp.AdminTypeID)}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">{emp.AdminTypeID === 4 ? '-' : (emp.LastLogin ? new Date(emp.LastLogin).toLocaleString() : 'Never')}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {emp.IsActive ? (
                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Active</span>
                                                    ) : (
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'}`}>Inactive</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex items-center gap-2" style={{ minWidth: '160px' }}>
                                                        <button
                                                            onClick={() => emp.IsActive ? handleDeactivate(emp) : handleReactivate(emp)}
                                                            className={`p-2 rounded-lg transition-colors ${
                                                                emp.IsActive
                                                                    ? 'text-red-600 hover:bg-red-50'
                                                                    : 'text-primary-600 hover:bg-primary-50'
                                                            }`}
                                                            title={emp.IsActive ? 'Deactivate' : 'Activate'}
                                                        >
                                                            {emp.IsActive ? <UserX size={18} /> : <UserCheck size={18} />}
                                                        </button>
                                                        <button
                                                            onClick={() => handleEdit(emp)}
                                                            className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                                                            title="Edit"
                                                        >
                                                            <PencilIcon size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => handlePasswordReset(emp)}
                                                            className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                                                            title="Send Password Reset"
                                                        >
                                                            <MailCheck size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                                        {/* Password Reset Confirmation Modal */}
                                                        {resetConfirm.open && (
                                                            <div className="modal-overlay">
                                                                <div className={`modal-content ${isDarkMode ? 'bg-slate-800 text-slate-100' : ''}`}>
                                                                    <h2 className="text-lg font-bold mb-4">Send Password Reset</h2>
                                                                    {/* <p className="mb-4">Send a password reset link to <span className="font-semibold">{resetConfirm.employee?.Email}</span>?</p> */}
                                                                    {/* The email holds a code, not a link: it's entered under Forgot password */}
                                                                    <p className="mb-4">Email a password reset code to <span className="font-semibold">{resetConfirm.employee?.Email}</span>? On the sign-in page they choose “Forgot password?”, then “Already have a code?”, and enter it.</p>
                                                                    <div className="flex gap-2 justify-end">
                                                                        <button className={`px-3 py-1 rounded ${isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`} onClick={handleCancelReset}>Cancel</button>
                                                                        <button className="px-3 py-1 rounded bg-primary-600 text-white" onClick={handleConfirmReset}>Send</button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Password Reset Success Toast */}
                                                        {resetSuccess && (
                                                            <div className="fixed bottom-6 right-6 bg-primary-600 text-white px-4 py-2 rounded shadow-lg z-50">
                                                                {/* Password reset link sent! */}
                                                                Reset code sent!
                                                            </div>
                                                        )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                        <div className={`px-6 py-4 border-t flex items-center justify-between ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total} employees
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                                >
                                    Prev
                                </button>
                                <span className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className={`p-2 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-300 hover:bg-slate-50'}`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                    </>
                )
            )}
            {activeTab === 'partners' && (
                partnersLoading ? loadingSpinner : (
                    <>
                        <div className="flex items-center mb-4">
                            <input
                                type="text"
                                placeholder="Search partners..."
                                className={`border rounded px-3 py-2 pl-8 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-500' : ''}`}
                                value={partnerSearch}
                                onChange={e => setPartnerSearch(e.target.value)}
                            />
                            <span style={{ position: 'relative', left: '-2.2rem', pointerEvents: 'none' }}>
                                <Search className="w-4 h-4 text-gray-400" />
                            </span>
                        </div>
                        <div className={`overflow-x-auto rounded shadow ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                            <table className={`min-w-full divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-200'}`}>
                                <thead className={isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}>
                                    <tr>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Name</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Email</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Mentees</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Joined</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Status</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Visible</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'bg-slate-800 divide-slate-700' : 'bg-white divide-gray-200'}`}>
                                    {partners.filter(p => {
                                        const s = partnerSearch.toLowerCase();
                                        return !s || p.FirstName?.toLowerCase().includes(s) || p.LastName?.toLowerCase().includes(s) || p.Email?.toLowerCase().includes(s);
                                    }).length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className={`px-6 py-4 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>No partners found.</td>
                                        </tr>
                                    ) : (
                                        partners.filter(p => {
                                            const s = partnerSearch.toLowerCase();
                                            return !s || p.FirstName?.toLowerCase().includes(s) || p.LastName?.toLowerCase().includes(s) || p.Email?.toLowerCase().includes(s);
                                        }).map(p => (
                                            <tr key={p.PartnerID}>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br from-primary-500 to-teal-600`}>
                                                            {p.FirstName?.[0]}{p.LastName?.[0]}
                                                        </div>
                                                        {p.FirstName} {p.LastName}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">{p.Email}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                                                        <Users size={12} /> {p.MenteeCount || 0}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">{p.JoinedDate}</td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {p.IsActive ? (
                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Active</span>
                                                    ) : (
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'}`}>Inactive</span>
                                                    )}
                                                    {!p.IsActive && !p.EmailVerified && (
                                                        <span className="ml-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800" title="This coach never entered the code we emailed them">Email not verified</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {p.IsVisible ? (
                                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">Visible</span>
                                                    ) : (
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500'}`}>Hidden</span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex items-center gap-1">
                                                        {!p.IsActive && (
                                                            <button
                                                                onClick={() => handleActivatePartner(p)}
                                                                className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 transition-colors"
                                                                title="Approve & activate this coach"
                                                            >
                                                                <UserCheck size={18} />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => setCredentialModal({ open: true, partner: p })}
                                                            className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
                                                            title="View credential"
                                                        >
                                                            <Award size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => setProfileModal({ open: true, partner: p })}
                                                            className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
                                                            title="View profile"
                                                        >
                                                            <UserCircle size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => togglePartnerVisibility(p)}
                                                            className={`p-2 rounded-lg transition-colors ${p.IsVisible ? 'text-amber-600 hover:bg-amber-50' : 'text-primary-600 hover:bg-primary-50'}`}
                                                            title={p.IsVisible ? 'Hide from students' : 'Show to students'}
                                                        >
                                                            {p.IsVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </>
                )
            )}
            {/* Approve/Activate Coach Confirmation Modal (reuses the app confirm-modal pattern) */}
            {approveCoachConfirm.open && (
                <div className="modal-overlay">
                    <div className={`modal-content ${isDarkMode ? 'bg-slate-800 text-slate-100' : ''}`}>
                        <h2 className="text-lg font-bold mb-4">Approve Coach</h2>
                        <p className="mb-4">
                            Approve and activate <span className="font-semibold">{approveCoachConfirm.partner?.FirstName} {approveCoachConfirm.partner?.LastName}</span>? They'll be able to sign in and will appear in Find a Coach.
                        </p>
                        {!approveCoachConfirm.partner?.EmailVerified && (
                            <div className={`mb-4 rounded border p-3 text-sm ${isDarkMode ? 'border-amber-700/50 bg-amber-900/20 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                                This coach never verified their email address, so we can't be sure the email belongs to them.
                                <label className="mt-2 flex items-center gap-2">
                                    <input type="checkbox" checked={approveCoachOverride} onChange={e => setApproveCoachOverride(e.target.checked)} />
                                    I checked this coach another way. Approve anyway.
                                </label>
                            </div>
                        )}
                        <div className="flex gap-2 justify-end">
                            <button className={`px-3 py-1 rounded ${isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`} onClick={handleCancelActivate}>Cancel</button>
                            {/*old code
                            <button className="px-3 py-1 rounded bg-primary-600 text-white" onClick={handleConfirmActivate}>Approve</button>
                            */}
                            <button
                                className="px-3 py-1 rounded bg-primary-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleConfirmActivate}
                                disabled={!approveCoachConfirm.partner?.EmailVerified && !approveCoachOverride}
                            >
                                Approve
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Coach Credential modal */}
            {credentialModal.open && (
                <div className="modal-overlay">
                    <div className={`modal-content ${isDarkMode ? 'bg-slate-800 text-slate-100' : ''}`}>
                        <h2 className="text-lg font-bold mb-1">Coach Credential</h2>
                        <p className="text-sm text-slate-500 mb-4">{credentialModal.partner?.FirstName} {credentialModal.partner?.LastName}</p>
                        <div className="text-sm">
                            {credentialModal.partner?.InvitedByOrg && (
                                <DetailRow label="Invited by">
                                    {credentialModal.partner.InvitedByOrg} <span className="text-slate-500">(certification optional for invited coaches)</span>
                                </DetailRow>
                            )}
                            <DetailRow label="Credential type">
                                {credentialModal.partner?.CredentialType === 'ICF' ? 'International Coaching Federation (ICF)'
                                    : credentialModal.partner?.CredentialType === 'Gallup' ? 'Gallup StrengthFinder'
                                    : (credentialModal.partner?.CredentialType || '—')}
                            </DetailRow>
                            <DetailRow label="Certificate URL">
                                {credentialModal.partner?.CertificateUrl ? (
                                    <a href={credentialModal.partner.CertificateUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">
                                        Open link <ExternalLink size={14} />
                                    </a>
                                ) : '—'}
                            </DetailRow>
                            <DetailRow label="Company">{credentialModal.partner?.CompanyName || '—'}</DetailRow>
                            <DetailRow label="State">{credentialModal.partner?.State || '—'}</DetailRow>
                            <DetailRow label="Directory email">{credentialModal.partner?.DirectoryEmail || '—'}</DetailRow>
                            <DetailRow label="Email verified">{credentialModal.partner?.EmailVerified ? 'Yes' : 'No'}</DetailRow>
                            <DetailRow label="Attested">
                                {credentialModal.partner?.IsAttest ? `Yes${credentialModal.partner?.CoachAttestDatetime ? ' · ' + credentialModal.partner.CoachAttestDatetime : ''}` : 'No'}
                            </DetailRow>
                        </div>
                        <div className="flex justify-end mt-5">
                            <button className={`px-3 py-1 rounded ${isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`} onClick={() => setCredentialModal({ open: false, partner: null })}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Coach Profile modal */}
            {profileModal.open && (
                <div className="modal-overlay">
                    <div className={`modal-content ${isDarkMode ? 'bg-slate-800 text-slate-100' : ''}`}>
                        <h2 className="text-lg font-bold mb-1">Coach Profile</h2>
                        <p className="text-sm text-slate-500 mb-4">{profileModal.partner?.FirstName} {profileModal.partner?.LastName}</p>
                        <div className="text-sm">
                            <DetailRow label="Sign-in email">{profileModal.partner?.Email || '—'}</DetailRow>
                            <DetailRow label="Directory email">{profileModal.partner?.DirectoryEmail || '—'}</DetailRow>
                            <DetailRow label="Company">{profileModal.partner?.CompanyName || '—'}</DetailRow>
                            <DetailRow label="State">{profileModal.partner?.State || '—'}</DetailRow>
                            <DetailRow label="LinkedIn">
                                {profileModal.partner?.LinkedInURL ? (
                                    <a href={profileModal.partner.LinkedInURL} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1">Open <ExternalLink size={14} /></a>
                                ) : '—'}
                            </DetailRow>
                            <DetailRow label="Time zone">{profileModal.partner?.TimeZone || '—'}</DetailRow>
                            <DetailRow label="Joined">{profileModal.partner?.JoinedDate || '—'}</DetailRow>
                            <DetailRow label="Mentees">{profileModal.partner?.MenteeCount ?? 0}</DetailRow>
                            <DetailRow label="Status">{profileModal.partner?.IsActive ? 'Active' : 'Inactive (pending)'}</DetailRow>
                            <DetailRow label="Directory listing">{profileModal.partner?.IsVisible ? 'Visible' : 'Hidden'}</DetailRow>
                        </div>
                        <div className="flex justify-end mt-5">
                            <button className={`px-3 py-1 rounded ${isDarkMode ? 'bg-slate-600 text-slate-200' : 'bg-slate-200 text-slate-700'}`} onClick={() => setProfileModal({ open: false, partner: null })}>Close</button>
                        </div>
                    </div>
                </div>
            )}
            {activeTab === 'permissions' && (
                <PermissionMatrixTable />
            )}
            {activeTab === 'employer-applications' && (
                <>
                    {/* Approve modal — link or create Employers row */}
                    {approveModal.open && (
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[200]">
                            <div className={`rounded-xl p-6 shadow-xl w-full max-w-lg ${isDarkMode ? 'bg-slate-800 text-slate-100' : 'bg-white'}`}>
                                <h2 className="text-lg font-bold mb-1">{approveModal.mode === 'link' ? 'Link Company' : 'Approve Employer Application'}</h2>
                                <p className={`text-sm mb-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    Approving <strong>{approveModal.app?.FirstName} {approveModal.app?.LastName}</strong> — <em>{approveModal.app?.CompanyName}</em>
                                </p>

                                <label className="block text-sm font-medium mb-1">Link to existing company</label>
                                <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                    Search and select a company already in the database, or leave blank to create a new one from the application.
                                </p>

                                {approveModal.loadingEmployers ? (
                                    <div className="text-sm text-slate-400 py-2">Loading companies…</div>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            className={`w-full border rounded-lg px-3 py-2 text-sm mb-2 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : ''}`}
                                            placeholder="Search companies…"
                                            value={approveModal.search}
                                            onChange={e => setApproveModal(m => ({ ...m, search: e.target.value, employerId: '' }))}
                                        />
                                        {approveModal.search.trim() && (
                                            <div className={`border rounded-lg max-h-48 overflow-y-auto mb-3 ${isDarkMode ? 'border-slate-600 bg-slate-700' : 'border-slate-200 bg-white'}`}>
                                                {approveModal.employers
                                                    .filter(e => e.Name.toLowerCase().includes(approveModal.search.toLowerCase()))
                                                    .slice(0, 20)
                                                    .map(e => (
                                                        <button
                                                            key={e.EmployerID}
                                                            type="button"
                                                            onClick={() => setApproveModal(m => ({ ...m, employerId: String(e.EmployerID), search: e.Name }))}
                                                            className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${isDarkMode ? 'hover:bg-primary-900/30 text-slate-200' : ''} ${String(approveModal.employerId) === String(e.EmployerID) ? (isDarkMode ? 'bg-primary-900/40 text-primary-300' : 'bg-primary-50 text-primary-700') : ''}`}
                                                        >
                                                            {e.Name}
                                                            {e.Location ? <span className={`ml-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`}>{e.Location}</span> : null}
                                                        </button>
                                                    ))}
                                                {approveModal.employers.filter(e => e.Name.toLowerCase().includes(approveModal.search.toLowerCase())).length === 0 && (
                                                    <div className={`px-3 py-2 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No match — a new company will be created.</div>
                                                )}
                                            </div>
                                        )}
                                        {approveModal.employerId ? (
                                            <p className="text-xs text-primary-600 mb-3">Will link to existing company (ID {approveModal.employerId}).</p>
                                        ) : (
                                            <p className={`text-xs mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>No company selected — a new entry will be created using "<strong>{approveModal.app?.CompanyName}</strong>".</p>
                                        )}
                                    </>
                                )}

                                <div className="flex gap-2 justify-end">
                                    <button
                                        className={`px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-700'}`}
                                        onClick={() => setApproveModal({ open: false, mode: 'approve', app: null, employerId: '', search: '', employers: [], loadingEmployers: false })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="px-4 py-2 rounded-lg bg-primary-600 text-white font-medium"
                                        onClick={handleApproveEmployer}
                                    >
                                        {approveModal.mode === 'link' ? 'Link' : 'Approve'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Reject reason modal */}
                    {rejectModal.open && (
                        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[200]">
                            <div className={`rounded-xl p-6 shadow-xl w-full max-w-md ${isDarkMode ? 'bg-slate-800 text-slate-100' : 'bg-white'}`}>
                                <h2 className="text-lg font-bold mb-3">Reject Application</h2>
                                <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Optionally provide a reason for rejection:</p>
                                <textarea
                                    className={`w-full border rounded-lg px-3 py-2 text-sm resize-none mb-4 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400' : ''}`}
                                    rows={3}
                                    placeholder="Reason (optional)"
                                    value={rejectModal.reason}
                                    onChange={e => setRejectModal(m => ({ ...m, reason: e.target.value }))}
                                />
                                <div className="flex gap-2 justify-end">
                                    <button
                                        className={`px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-200 text-slate-700'}`}
                                        onClick={() => setRejectModal({ open: false, appId: null, reason: '' })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium"
                                        onClick={handleRejectEmployer}
                                    >
                                        Reject
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Filter bar */}
                    <div className="flex items-center gap-3 mb-4">
                        {['pending', 'approved', 'rejected', ''].map(f => (
                            <button
                                key={f}
                                onClick={() => setEmployerAppsFilter(f)}
                                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                                    employerAppsFilter === f
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : isDarkMode
                                            ? 'border-slate-600 text-slate-400 hover:bg-slate-700'
                                            : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                                }`}
                            >
                                {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                            </button>
                        ))}
                    </div>

                    {employerAppsLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500" />
                        </div>
                    ) : (
                        <div className={`overflow-x-auto rounded shadow ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className={isDarkMode ? 'bg-slate-700' : 'bg-gray-50'}>
                                    <tr>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Applicant</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Company</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Job Title</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Submitted</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Status</th>
                                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody className={`divide-y ${isDarkMode ? 'bg-slate-800 divide-slate-700' : 'bg-white divide-gray-200'}`}>
                                    {employerApps.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className={`px-6 py-8 text-center ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>No applications found.</td>
                                        </tr>
                                    ) : employerApps.map(app => (
                                        <tr key={app.ApplicationID} className={isDarkMode ? 'text-slate-200' : ''}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{app.FirstName} {app.LastName}</div>
                                                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{app.EmailAddress}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div>{app.CompanyName}</div>
                                                {app.CompanyWebsite && (
                                                    <div className={`text-xs truncate max-w-[160px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{app.CompanyWebsite}</div>
                                                )}
                                            </td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                                                {app.JobTitle || <span className={isDarkMode ? 'text-slate-600' : 'text-slate-300'}>—</span>}
                                            </td>
                                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                                {app.SubmittedAt || '—'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {app.Status === 'pending' && (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Pending</span>
                                                )}
                                                {app.Status === 'approved' && (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Approved</span>
                                                )}
                                                {app.Status === 'rejected' && (
                                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Rejected</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {app.Status === 'pending' && (
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => openApproveModal(app)}
                                                            className={`p-2 rounded-lg text-primary-600 transition-colors ${isDarkMode ? 'hover:bg-primary-900/30' : 'hover:bg-primary-50'}`}
                                                            title="Approve"
                                                        >
                                                            <CheckCircle size={18} />
                                                        </button>
                                                        <button
                                                            onClick={() => setRejectModal({ open: true, appId: app.ApplicationID, reason: '' })}
                                                            className={`p-2 rounded-lg text-red-600 transition-colors ${isDarkMode ? 'hover:bg-red-900/30' : 'hover:bg-red-50'}`}
                                                            title="Reject"
                                                        >
                                                            <XCircle size={18} />
                                                        </button>
                                                    </div>
                                                )}
                                                {app.Status === 'approved' && (
                                                    <button
                                                        onClick={() => openApproveModal(app, 'link')}
                                                        className={`p-2 rounded-lg text-blue-600 transition-colors ${isDarkMode ? 'hover:bg-blue-900/30' : 'hover:bg-blue-50'}`}
                                                        title="Link Company"
                                                    >
                                                        <Link size={18} />
                                                    </button>
                                                )}
                                                {app.Status === 'rejected' && app.RejectionReason && (
                                                    <span className={`text-xs italic ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>"{app.RejectionReason}"</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

        </div>
    );
}
